import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { KineticText } from "@/components/KineticText";
import { Ornament } from "@/components/Ornament";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { TrackList } from "@/components/TrackList";
import { CHAPTERS, IMAGES, STREAMS, formatShowDate, splitShows } from "@/data/site";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "MusicGroup",
  name: "1001 Nights Orchestra",
  genre: ["Middle Eastern", "World", "Folk"],
  foundingDate: "1987",
  foundingLocation: {
    "@type": "Place",
    address: { "@type": "PostalAddress", addressLocality: "Austin", addressRegion: "TX", addressCountry: "US" },
  },
  description:
    "Austin's longest-running Middle Eastern ensemble, performing the music of the Middle East, Latin America and the Balkans since 1987.",
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[80vh] lg:min-h-[80vh] flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
        <img
          src={IMAGES.hero}
          alt="Andalusian zellige tile wall in midnight blue and gold, lit by candlelight"
          className="w-full h-full object-cover scale-110"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#070A12] via-[#070A12]/70 to-[#070A12]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#070A12]/80 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16 pt-32 w-full">
        <h1 className="font-serif text-4xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight text-amber-50 max-w-5xl">
          <KineticText
            lines={[
              "Where a Thousand",
              <span key="l2" className="italic gold-text">Melodies Meet.</span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Music of the Middle East, Latin America and the Balkans — brought together by one
          enduring Austin ensemble.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/shows"
            data-testid="hero-cta-shows"
            className="group inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-midnight font-mono text-xs uppercase tracking-[0.2em] hover:bg-gold-bright transition-colors duration-300"
          >
            See Upcoming Shows
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/media"
            data-testid="hero-cta-media"
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-amber-500/30 text-amber-100 font-mono text-xs uppercase tracking-[0.2em] hover:border-amber-400/70 hover:bg-amber-500/5 transition-all duration-300"
          >
            Watch & Listen
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 z-10"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 [writing-mode:vertical-rl]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-px h-14 bg-gradient-to-b from-amber-400/70 to-transparent"
        />
      </motion.div>
    </section>
  );
};

const Chapter = ({ chapter, index }) => {
  const flip = index % 2 === 1;
  const [lightbox, setLightbox] = useState(null);
  const prints = chapter.artifact
    ? [
        { src: chapter.image, alt: chapter.imageAlt, caption: chapter.caption },
        ...(chapter.secondImage ? [{ src: chapter.secondImage, alt: chapter.secondImageAlt, caption: chapter.secondCaption }] : []),
      ]
    : [];
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${flip ? "" : ""}`}>
      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        {chapter.artifact ? (
          <div
            data-testid={`chapter-artifact-${chapter.num}`}
            className="relative aspect-[4/3] border border-amber-500/15 bg-[#0a0f1c] flex items-center justify-center gap-3 sm:gap-6 px-4 overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.14), transparent 62%)" }} />
            <img
              src={chapter.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-20 blur-2xl scale-110"
            />
            <figure
              role="button"
              tabIndex={0}
              data-testid={`chapter-print-${chapter.num}-0`}
              onClick={() => setLightbox(0)}
              onKeyDown={(e) => e.key === "Enter" && setLightbox(0)}
              className={`relative cursor-zoom-in border border-amber-500/25 bg-midnight p-2.5 shadow-[0_30px_50px_-20px_rgba(0,0,0,0.9)] hover:rotate-0 transition-transform duration-500 ${chapter.secondImage ? "-rotate-2" : "-rotate-1"}`}
            >
              <img src={chapter.image} alt={chapter.imageAlt} loading="lazy" className="block w-auto object-contain" style={{ maxHeight: chapter.secondImage ? "min(34vw, 250px)" : "min(50vw, 330px)" }} />
              <figcaption className="pt-2.5 pb-0.5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400/80">
                {chapter.caption}
              </figcaption>
            </figure>
            {chapter.secondImage && (
              <figure
                role="button"
                tabIndex={0}
                data-testid={`chapter-print-${chapter.num}-1`}
                onClick={() => setLightbox(1)}
                onKeyDown={(e) => e.key === "Enter" && setLightbox(1)}
                className="relative cursor-zoom-in border border-amber-500/25 bg-midnight p-2.5 shadow-[0_30px_50px_-20px_rgba(0,0,0,0.9)] rotate-2 mt-8 hover:rotate-0 transition-transform duration-500"
              >
                <img src={chapter.secondImage} alt={chapter.secondImageAlt} loading="lazy" className="block w-auto object-contain" style={{ maxHeight: "min(34vw, 250px)" }} />
                <figcaption className="pt-2.5 pb-0.5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400/80">
                  {chapter.secondCaption}
                </figcaption>
              </figure>
            )}
            <Lightbox items={prints} index={lightbox} onChange={setLightbox} onClose={() => setLightbox(null)} testId={`chapter-lightbox-${chapter.num}`} />
          </div>
        ) : (
          <div className="relative group overflow-hidden border border-amber-500/15">
            <img
              src={chapter.image}
              alt={chapter.imageAlt}
              loading="lazy"
              className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A12]/70 to-transparent" />
          </div>
        )}
      </Reveal>
      <Reveal delay={0.15} className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
        <span className="font-serif text-7xl sm:text-8xl gold-outline leading-none">{chapter.num}</span>
        <h3 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-amber-100 tracking-tight">{chapter.title}</h3>
        <div className="mt-4 space-y-4 text-slate-300 leading-relaxed max-w-lg">
          {chapter.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </Reveal>
    </div>
  );
};

export default function Home() {
  const { upcoming } = splitShows();

  return (
    <main>
      <Seo
        title="1001 Nights Orchestra — Middle Eastern Ensemble in Austin, TX"
        siteName="1001 Nights Orchestra"
        description="Austin's longest-running Middle Eastern ensemble since 1987. Upcoming shows, performance videos, music and booking inquiries."
        jsonLd={jsonLd}
      />
      <Hero />

      <section data-testid="manifesto-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-4 lg:pt-6 pb-20 lg:pb-32 space-y-24 lg:space-y-36">
        <Reveal className="-mb-10 lg:-mb-20">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">The Story in Three Chapters</p>
          <p data-testid="story-subtext" className="mt-4 font-serif italic text-2xl sm:text-3xl text-amber-100/90 tracking-tight max-w-2xl">
            Where it started, how it grew, and where it's going.
          </p>
        </Reveal>
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.num} chapter={c} index={i} />
        ))}
      </section>

      <section data-testid="featured-media-section" className="border-y border-amber-500/10 bg-[#0a0f1c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">Featured Media</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-amber-50 tracking-tight">
              Explore Our Sound.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Sample the repertoire, then find full albums on your streaming service of choice.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={STREAMS.spotify} target="_blank" rel="noopener noreferrer" data-testid="stream-link-spotify" className="inline-flex items-center gap-2 text-sm text-amber-200 hover:text-amber-100 border border-amber-500/25 hover:border-amber-400/60 px-5 py-2.5 transition-all">
                Spotify <ArrowUpRight size={14} />
              </a>
              <a href={STREAMS.apple} target="_blank" rel="noopener noreferrer" data-testid="stream-link-apple" className="inline-flex items-center gap-2 text-sm text-amber-200 hover:text-amber-100 border border-amber-500/25 hover:border-amber-400/60 px-5 py-2.5 transition-all">
                Apple Music <ArrowUpRight size={14} />
              </a>
              <a href={STREAMS.youtube} target="_blank" rel="noopener noreferrer" data-testid="stream-link-youtube" className="inline-flex items-center gap-2 text-sm text-amber-200 hover:text-amber-100 border border-amber-500/25 hover:border-amber-400/60 px-5 py-2.5 transition-all">
                YouTube <ArrowUpRight size={14} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <TrackList idPrefix="track" />
            <p className="mt-3 text-xs text-slate-600">Streaming via SoundCloud — tap a title to play right here.</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="shows-preview-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">On Stage Soon</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 tracking-tight">Upcoming Shows</h2>
          </div>
          <Link to="/shows" data-testid="shows-preview-all-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-300 hover:text-amber-100 transition-colors">
            All shows <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        {upcoming.length === 0 ? (
          <Reveal>
            <div
              data-testid="shows-preview-empty"
              className="relative overflow-hidden border border-amber-500/15 bg-surface/40 px-6 sm:px-12 py-14 sm:py-16"
            >
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 0% 50%, rgba(212,175,55,0.10), transparent 55%)" }} />
              <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                <div className="max-w-xl">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">Next dates coming soon</p>
                  <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-amber-50 tracking-tight leading-tight">
                    Between seasons, the orchestra plays <span className="italic gold-text">for you.</span>
                  </h3>
                  <p className="mt-4 text-slate-400 leading-relaxed">
                    Weddings, galas, festivals and private evenings — bring the ensemble to your event while the next public dates are being set.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 shrink-0">
                  <Link
                    to="/booking"
                    data-testid="shows-preview-book-cta"
                    className="inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-midnight font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-amber-300 transition-colors duration-300"
                  >
                    Book the orchestra <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/shows"
                    data-testid="shows-preview-past-link"
                    className="inline-flex items-center gap-2 px-7 py-3.5 border border-amber-500/30 text-amber-200 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-gold hover:text-amber-50 transition-colors duration-300"
                  >
                    Recent performances
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="border-t border-amber-500/10">
            {upcoming.slice(0, 3).map((s, i) => {
              const d = formatShowDate(s.date);
              return (
                <Reveal key={s.id} delay={i * 0.08}>
                  <Link
                    to="/shows"
                    data-testid={`show-preview-row-${s.id}`}
                    className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 sm:gap-8 py-6 border-b border-amber-500/10 border-l-2 border-l-transparent hover:border-l-gold hover:bg-surface/40 px-4 -mx-4 transition-all duration-300"
                  >
                    <div className="text-center w-16">
                      <span className="block font-mono text-2xl text-amber-300">{d.day}</span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{d.month}</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-serif text-xl sm:text-2xl text-amber-100 truncate group-hover:text-amber-50">{s.title}</h3>
                      <p className="mt-1 text-sm text-slate-400 flex items-center gap-1.5 truncate">
                        <MapPin size={13} className="text-amber-500/70 shrink-0" /> {s.venue} — {s.city}
                      </p>
                    </div>
                    <span className={`hidden sm:inline-block font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${s.status === "Sold Out" ? "border-crimson text-red-300/80" : "border-amber-500/30 text-amber-300"}`}>
                      {s.status || "Tickets"}
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>

      <section data-testid="booking-cta-section" className="relative overflow-hidden border-t border-amber-500/10">
        <img src={IMAGES.palace} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070A12] via-[#070A12]/85 to-[#070A12]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(139,30,45,0.35), transparent 60%)" }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-24 lg:py-36 text-center">
          <Reveal>
            <Ornament className="mb-10 max-w-md mx-auto" />
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-amber-50 tracking-tight leading-tight">
              Bring us
              <br />
              <span className="italic gold-text">to your stage.</span>
            </h2>
            <p className="mt-6 text-slate-300 max-w-xl mx-auto leading-relaxed">
              Weddings, festivals, gallery openings, silent-film scores and private galas —
              from an intimate quartet to a larger ensemble.
            </p>
            <Link
              to="/booking"
              data-testid="cta-book-band-btn"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-gold text-midnight font-mono text-xs uppercase tracking-[0.2em] hover:bg-gold-bright transition-colors duration-300"
            >
              Inquire About Booking <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
